import { useForm, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Dialog, FormItem, Button } from 'vnetwork-ui'
import { bulkUpsertActivityTargets } from '@/modules/Report/services'
import {
    UpsertActivityTargetPayload,
    UpsertSalesTargetPayload,
} from '@/modules/Report/types'
import { bulkUpsertSalesTargets } from '@/modules/Report/services'
import EmployeeSelect from '@/components/Form/Shared/EmployeeSelect'
import DepartmentSelect from '@/components/Form/Shared/DepartmentSelect'
import { getEmployeesOptions } from '@/modules/Employee/services'
import { useEffect, useMemo } from 'react'
import { ModalProps } from '@/types/shared'
import { useMutationWithHandler } from '@/hooks/useMutationWithHandler'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import ActivityTargetForm, {
    MonthlyTarget,
    createEmptyMonthsData,
} from '../components/ActivityTargetForm'
import toast from 'react-hot-toast'

export interface CreateActivityTargetModalProps extends ModalProps {
    defaultYear?: number
}

interface FormData {
    department_ids: number[]
    employee_ids: number[]
    target_year: number
    months: MonthlyTarget[]
    quickFill: {
        presentation: number
        poc: number
        contract: number
        revenue: number
    }
}

const CreateActivityTargetModal = ({
    open,
    onClose,
    defaultYear,
}: CreateActivityTargetModalProps) => {
    const { t } = useTranslation('report')
    const currentYear = defaultYear || new Date().getFullYear()

    const { control, handleSubmit, reset, setValue, register, getValues } =
        useForm<FormData>({
            defaultValues: {
                department_ids: [],
                employee_ids: [],
                target_year: currentYear,
                months: createEmptyMonthsData(),
                quickFill: { presentation: 0, poc: 0, contract: 0, revenue: 0 },
            },
        })

    const selectedEmployees = useWatch({ control, name: 'employee_ids' })

    // Reset form when modal opens
    useEffect(() => {
        if (open) {
            reset({
                department_ids: [],
                employee_ids: [],
                target_year: defaultYear || new Date().getFullYear(),
                months: createEmptyMonthsData(),
                quickFill: { presentation: 0, poc: 0, contract: 0, revenue: 0 },
            })
        }
    }, [open, defaultYear, reset])

    // Handle department change - auto-fill employees
    const handleDepartmentChange = async (departmentIds?: number[]) => {
        if (!departmentIds || departmentIds.length === 0) {
            setValue('employee_ids', [], { shouldDirty: true })
            return
        }
        const employees = await getEmployeesOptions('', departmentIds, 'active')
        setValue(
            'employee_ids',
            employees?.map((emp) => emp.value as number) ?? [],
            { shouldDirty: true }
        )
    }

    const { mutateAsync, isPending } = useMutationWithHandler(
        {
            mutationFn: bulkUpsertActivityTargets,
            onMutate: () => ({}),
        },
        {
            reactQueryKeys: [
                QUERY_KEYS.EMPLOYEE_DASHBOARD,
                QUERY_KEYS.ACTIVITY_TARGETS,
            ],
            onSuccess() {
                toast.success(
                    t('target_analytics.activityTarget.successMessage')
                )
            },
            onError() {
                toast.error(t('target_analytics.activityTarget.errorMessage'))
            },
        }
    )

    const { mutateAsync: mutateSalesAsync } = useMutationWithHandler(
        {
            mutationFn: bulkUpsertSalesTargets,
            onMutate: () => ({}),
        },
        {
            reactQueryKeys: [
                QUERY_KEYS.EMPLOYEE_DASHBOARD,
                QUERY_KEYS.ACTIVITY_TARGETS,
            ],
            onSuccess() {
                toast.success(t('target_analytics.saleTarget.successMessage'))
            },
            onError() {
                toast.error(t('target_analytics.saleTarget.errorMessage'))
            },
        }
    )

    const onSubmit = async (data: FormData) => {
        if (!data.employee_ids || data.employee_ids.length === 0) return

        const targets: UpsertActivityTargetPayload[] = []
        const salesTargets: UpsertSalesTargetPayload[] = []

        // Generate targets for each employee × month × activity type
        data.employee_ids.forEach((empId) => {
            data.months.forEach((monthData) => {
                if (monthData.presentation > 0) {
                    targets.push({
                        employee_id: empId,
                        activity_type: 'presentation',
                        target_quantity: monthData.presentation || 0,
                        target_scope: 'month',
                        target_year: data.target_year,
                        target_month: monthData.month,
                    })
                }
                if (monthData.poc > 0) {
                    targets.push({
                        employee_id: empId,
                        activity_type: 'poc',
                        target_quantity: monthData.poc || 0,
                        target_scope: 'month',
                        target_year: data.target_year,
                        target_month: monthData.month,
                    })
                }
                if (monthData.contract > 0) {
                    targets.push({
                        employee_id: empId,
                        activity_type: 'contract',
                        target_quantity: monthData.contract || 0,
                        target_scope: 'month',
                        target_year: data.target_year,
                        target_month: monthData.month,
                    })
                }

                // Add revenue target (only if amount > 0)
                if (monthData.revenue && monthData.revenue > 0) {
                    salesTargets.push({
                        employee_id: empId,
                        target_amount: monthData.revenue,
                        target_scope: 'month',
                        target_year: data.target_year,
                        target_month: monthData.month,
                        is_active: true,
                    })
                }
            })
        })

        // Call APIs only if they have data
        const promises = []

        if (targets.length > 0) {
            promises.push(mutateAsync({ targets }))
        }

        if (salesTargets.length > 0) {
            promises.push(mutateSalesAsync({ targets: salesTargets }))
        }

        try {
            if (promises.length > 0) {
                await Promise.all(promises)
                reset()
                onClose()
            } else {
                // No targets to save, just close modal
                reset()
                onClose()
            }
        } catch (error) {
            // Error handled by useMutationWithHandler (toast)
            console.error('Failed to save targets', error)
        }
    }

    const YEAR_OPTIONS = useMemo(
        () =>
            Array.from({ length: 10 }, (_, i) => ({
                label: `${currentYear - 5 + i}`,
                value: currentYear - 5 + i,
            })),
        [currentYear]
    )

    return (
        <Dialog open={open} onClose={onClose} title={t('create')} maxWidth="md">
            <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col gap-4"
            >
                {/* Header Row */}
                <div className="grid grid-cols-3 gap-4">
                    <DepartmentSelect
                        control={control}
                        name="department_ids"
                        label={t('department')}
                        multiple
                        onChangeAfterHookForm={handleDepartmentChange}
                    />
                    <EmployeeSelect
                        control={control}
                        name="employee_ids"
                        label={t('employee')}
                        multiple
                    />
                    <FormItem
                        control={control}
                        name="target_year"
                        type="select"
                        label={t('year')}
                        options={YEAR_OPTIONS}
                    />
                </div>

                {/* Shared Form Component */}
                <ActivityTargetForm
                    control={control}
                    register={register}
                    setValue={setValue}
                    getValues={getValues}
                />

                {/* Actions */}
                <div className="flex justify-end gap-3 pb-4">
                    <Button
                        type="button"
                        variant="outlined"
                        onClick={onClose}
                        disabled={isPending}
                    >
                        {t('cancel')}
                    </Button>
                    <Button
                        type="submit"
                        variant="contained"
                        loading={isPending}
                        disabled={
                            isPending ||
                            !selectedEmployees ||
                            selectedEmployees.length === 0
                        }
                    >
                        {t('create')}
                    </Button>
                </div>
            </form>
        </Dialog>
    )
}

export default CreateActivityTargetModal
